import "@/App.css";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import InstallPrompt from "@/components/InstallPrompt";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import i18n, { idiomaElegido } from "@/i18n";

import Landing from "@/pages/Landing";
import { AsistenteWidget } from "@/components/asistente/AsistenteWidget";
import Legal from "@/pages/Legal";
import CerrarSesionesCeo from "@/pages/CerrarSesionesCeo";
import { CookieBanner } from "@/components/cookies/CookieBanner";
import FirmarContrato from "@/pages/FirmarContrato";
import FirmarCertificacion from "@/pages/FirmarCertificacion";
import PortalCliente from "@/pages/PortalCliente";
import AceptarInvitacion from "@/pages/AceptarInvitacion";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import { ConFuncion } from "@/components/planes/FuncionBloqueada";
import { REDIRECCIONES, RUTAS_PUBLICAS } from "@/publico/enlaces";
import NoEncontrada from "@/pages/NoEncontrada";

// Web pública (blog, FAQ, páginas de contenido): trozo aparte, no se descarga en la portada.
const Publica = lazy(() => import("@/publico/Publica"));
const FirmarAlbaran = lazy(() => import("@/pages/FirmarAlbaran"));

// Páginas privadas (tras login) y la PWA de fichaje (usa leaflet): se descargan bajo demanda.
const AppLayout = lazy(() => import("@/components/layout/AppLayout"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Clientes = lazy(() => import("@/pages/Clientes"));
const Presupuestos = lazy(() => import("@/pages/Presupuestos"));
const Biblioteca = lazy(() => import("@/pages/Biblioteca"));
const Facturas = lazy(() => import("@/pages/Facturas"));
const Envios = lazy(() => import("@/pages/Envios"));
const Gastos = lazy(() => import("@/pages/Gastos"));
const Obras = lazy(() => import("@/pages/Obras"));
const Jornada = lazy(() => import("@/pages/Jornada"));
const Trabajadores = lazy(() => import("@/pages/Trabajadores"));
const DescargarApp = lazy(() => import("@/pages/DescargarApp"));
const FichajeApp = lazy(() => import("@/pages/FichajeApp"));
const FichajeWorker = lazy(() => import("@/pages/FichajeWorker"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const Bancos = lazy(() => import("@/pages/Bancos"));
const Trimestral = lazy(() => import("@/pages/Trimestral"));
const Morosidad = lazy(() => import("@/pages/Morosidad"));
const Afiliados = lazy(() => import("@/pages/Afiliados"));
const Comparador = lazy(() => import("@/pages/Comparador"));
const Nuvli = lazy(() => import("@/pages/Nuvli"));
const SolicitudesOferta = lazy(() => import("@/pages/SolicitudesOferta"));
const SolicitudOferta = lazy(() => import("@/pages/SolicitudOferta"));
const ComparativaOfertas = lazy(() => import("@/pages/ComparativaOfertas"));
const NuevaSolicitudOferta = lazy(() => import("@/pages/NuevaSolicitudOferta"));
const OfertasPresupuesto = lazy(() => import("@/pages/OfertasPresupuesto"));
const VistaGlobal = lazy(() => import("@/pages/VistaGlobal"));
const Contratos = lazy(() => import("@/pages/Contratos"));
const Certificaciones = lazy(() => import("@/pages/Certificaciones"));
const Configuracion = lazy(() => import("@/pages/Configuracion"));
const CentroAyuda = lazy(() => import("@/pages/CentroAyuda"));
const MisReferidos = lazy(() => import("@/pages/MisReferidos"));
const Planes = lazy(() => import("@/pages/Planes"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminSeo = lazy(() => import("@/pages/AdminSeo"));
const WhatsApp = lazy(() => import("@/pages/WhatsApp"));
const Reformas = lazy(() => import("@/pages/Reformas"));
const SolicitudesReformas = lazy(() => import("@/pages/SolicitudesReformas"));
const AdministracionCeo = lazy(() => import("@/pages/AdministracionCeo"));

function Cargando() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-sky-50 to-[#E0F2FE]/40" data-testid="app-cargando">
      <Loader2 className="w-8 h-8 animate-spin text-[#0284C7]" />
    </div>
  );
}

// Solo descarga el panel si el servidor confirma el acceso; para el resto es una ruta inexistente.
function AccesoReservado() {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    const comprobar = () => api.get("/ceo/estado");
    comprobar().catch(() => api.post("/auth/refresh").then(comprobar)).then(() => setOk(true)).catch(() => setOk(false));
  }, []);
  if (ok === false) return <Navigate to="/dashboard" replace />;
  return ok ? <Suspense fallback={null}><AdministracionCeo /></Suspense> : null;
}

// Solo administradores; el resto vuelve al Dashboard (igual que /admin/seo y /ceo).
function SoloAdmin({ children }) {
  const { user } = useAuth();
  return user?.role === "admin" ? children : <Navigate to="/dashboard" replace />;
}

function Protected({ children }) {
  const { user } = useAuth();
  // Dentro de la app, el idioma elegido a mano; al volver a la web pública, español.
  useLayoutEffect(() => {
    i18n.changeLanguage(idiomaElegido());
    return () => { i18n.changeLanguage("es"); };
  }, []);
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-sky-50 to-[#E0F2FE]/40">
        <Loader2 className="w-8 h-8 animate-spin text-[#0284C7]" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Página trasladada: lleva a la nueva conservando ?parámetros y #ancla.
function Redirige({ a }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: a, search, hash }} replace />;
}

function App() {
  useEffect(() => {
    const s = document.getElementById("app-splash");
    if (s) { s.style.opacity = "0"; setTimeout(() => s.remove(), 400); }
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Cargando />}>
          <Routes>
            <Route path="/"element={<><Landing /><AsistenteWidget publico /></>} />
            {RUTAS_PUBLICAS.map(([ruta, pagina]) => <Route key={ruta} path={ruta} element={<><Publica pagina={pagina} /><AsistenteWidget publico /></>} />)}
            {Object.entries(REDIRECCIONES).map(([de, a]) => <Route key={de} path={de} element={<Redirige a={a} />} />)}
            <Route path="/login" element={<Landing />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/cerrar-sesiones-ceo" element={<CerrarSesionesCeo />} />
            <Route path="/desactivar-estricto-ceo" element={<CerrarSesionesCeo modo="estricto" />} />
            <Route path="/desactivar-cerebro-ceo" element={<CerrarSesionesCeo modo="cerebro" />} />
            <Route path="/reactivar-cerebro-ceo" element={<CerrarSesionesCeo modo="cerebro_on" />} />
            <Route path="/app" element={<FichajeWorker />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/invitacion/:token" element={<AceptarInvitacion />} />
            <Route path="/contrato/firmar/:token" element={<FirmarContrato />} />
            <Route path="/certificacion/:token" element={<FirmarCertificacion />} />
            <Route path="/albaran/:token" element={<FirmarAlbaran />} />
            <Route path="/portal/:token" element={<PortalCliente />} />
            <Route
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/clientes" element={<Clientes />} />
              <Route path="/presupuestos" element={<Presupuestos />} />
              <Route path="/presupuestos/:pid/solicitud-oferta" element={<NuevaSolicitudOferta />} />
              <Route path="/presupuestos/:pid/ofertas" element={<OfertasPresupuesto />} />
              <Route path="/presupuestos/:pid/vista-global" element={<VistaGlobal />} />
              <Route path="/solicitudes-oferta" element={<SolicitudesOferta />} />
              <Route path="/solicitudes-oferta/:id" element={<SolicitudOferta />} />
              <Route path="/solicitudes-oferta/comparar/:pid" element={<ComparativaOfertas />} />
              <Route path="/biblioteca" element={<Biblioteca />} />
              <Route path="/facturas" element={<Facturas />} />
              <Route path="/envios" element={<ConFuncion funcion="envios"><Envios /></ConFuncion>} />
              <Route path="/gastos" element={<Gastos />} />
              <Route path="/obras" element={<Obras />} />
              <Route path="/certificaciones" element={<ConFuncion funcion="certificaciones"><Certificaciones /></ConFuncion>} />
              <Route path="/jornada" element={<ConFuncion funcion="fichaje"><Jornada /></ConFuncion>} />
              <Route path="/trabajadores" element={<ConFuncion funcion="fichaje"><Trabajadores /></ConFuncion>} />
              <Route path="/descargar-app" element={<DescargarApp />} />
              <Route path="/fichaje-app" element={<ConFuncion funcion="fichaje"><FichajeApp /></ConFuncion>} />
              <Route path="/agenda" element={<ConFuncion funcion="agenda"><Agenda /></ConFuncion>} />
              <Route path="/bancos" element={<Bancos />} />
              <Route path="/trimestral" element={<Trimestral />} />
              <Route path="/morosidad" element={<ConFuncion funcion="morosidad"><Morosidad /></ConFuncion>} />
              <Route path="/afiliados" element={<Afiliados />} />
              <Route path="/configuracion" element={<Configuracion />} />
              <Route path="/ayuda" element={<CentroAyuda />} />
              <Route path="/referidos" element={<MisReferidos />} />
              <Route path="/planes" element={<Planes />} />
              <Route path="/admin" element={<SoloAdmin><Admin /></SoloAdmin>} />
              <Route path="/admin/seo" element={<SoloAdmin><AdminSeo /></SoloAdmin>} />
              <Route path="/ceo" element={<AccesoReservado />} />
              <Route path="/whatsapp" element={<WhatsApp />} />
              <Route path="/reformas" element={<Reformas />} />
              <Route path="/solicitudes-reformas" element={<SolicitudesReformas />} />
              <Route path="/comparador" element={<Comparador />} />
              <Route path="/nuvli" element={<ConFuncion funcion="secretaria"><Nuvli /></ConFuncion>} />
              <Route path="/ok-nuvli" element={<Navigate to={{ pathname: "/nuvli", search: window.location.search }} replace />} />
              <Route path="/contratos" element={<Contratos />} />
            </Route>
            <Route path="*" element={<NoEncontrada />} />
          </Routes>
          </Suspense>
          <CookieBanner />
          <InstallPrompt />
        </BrowserRouter>
        <Toaster position="top-right" richColors offset="76px" />
      </AuthProvider>
    </div>
  );
}

export default App;
